import type { StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import { EntityBaseActions } from '@medsurf/flat-actions';

export type EntityBaseStateModel<T extends FlatModels.Base | FlatModels.UserEntityModels.User> = FlatModels.EntityStatesModels.EntityStateModel<T>;

export abstract class EntityBaseState<TEntity extends FlatModels.Base | FlatModels.UserEntityModels.User, TStateModel extends EntityBaseStateModel<TEntity>> {
  //<editor-fold desc="Selectors">

  static typedEntities$(state: any) {
    return state.entities;
  }

  static entities$(typedEntities: { [id: string]: any }) {
    return Object.values(typedEntities);
  }

  static entity$(typedEntities: { [id: string]: any }) {
    return (id: string) => {
      return typedEntities && typedEntities[id] !== undefined ? typedEntities[id] : null;
    };
  }

  static entitiesByIds$(typedEntities: { [id: string]: any }) {
    return (ids: string[]) => {
      return (ids || []).reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as any[]);
    };
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  getEntitySuccess(state: StateContext<TStateModel>,
                   {entityQueryResult}: EntityBaseActions.GetEntitySuccess) {
    // Get entities
    const entities = structuredClone(state.getState().entities);
    
    // Check Result
    let patchState = false;
    
    // Check for removed entities -> delete them
    if ((entityQueryResult as any).modifierType && (entityQueryResult as any).modifierType === FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE) {
      // Delete Entity
      patchState = true;
      delete entities[entityQueryResult.entity.id];
    }
    // Ensure we only save the entity once -> so the history don't get destroyed
    else if (entities[entityQueryResult.entity.id] === undefined) {
      // Add Entity
      patchState = true;
      entities[entityQueryResult.entity.id] = entityQueryResult.entity as TEntity;
    }

    // Patch State
    if (patchState) {
      state.patchState({
        entities
      } as TStateModel);
    }
  }

  getEntitiesSuccess(state: StateContext<TStateModel>,
                     {entityQueryResults}: EntityBaseActions.GetEntitiesSuccess) {
    // Get entities
    const entities = structuredClone(state.getState().entities);

    // Iterate through the results
    let patchState = false;
    for (const entityQueryResult of entityQueryResults) {
      // Check for removed entities -> delete them
      if ((entityQueryResult as any).modifierType && (entityQueryResult as any).modifierType === FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE) {
        // Delete Entity
        patchState = true;
        delete entities[entityQueryResult.entity.id];
      }
      // Ensure we only save the entity once -> so the history don't get destroyed
      else if (entities[entityQueryResult.entity.id] === undefined) {
        // Add Entity
        patchState = true;
        entities[entityQueryResult.entity.id] = entityQueryResult.entity as TEntity;
      }
    }

    // Patch State
    if (patchState) {
      state.patchState({
        entities: entities
      } as TStateModel);
    }
  }

  modifyEntitySuccess(state: StateContext<TStateModel>,
                      {saveSnapshotItem}: EntityBaseActions.ModifyEntitySuccess) {
    // Get entities
    const entities = structuredClone(state.getState().entities);

    switch (saveSnapshotItem.modifierType) {
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD:
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE:
        entities[saveSnapshotItem.entity.id] = saveSnapshotItem.entity as TEntity;
        break;
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE:
        delete entities[saveSnapshotItem.entity.id];
        break;
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          "notification.type_not_supported",
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.EntityBaseState.modifyEntitiesSuccess"
        );
    }

    // Patch State
    state.patchState({
      entities
    } as TStateModel);
  }

  modifyEntitiesSuccess(state: StateContext<TStateModel>,
                        {saveSnapshotItems}: EntityBaseActions.ModifyEntitiesSuccess) {
    // Get entities
    const entities = structuredClone(state.getState().entities);

    // Iterate through the results
    for (const saveSnapshotItem of saveSnapshotItems) {
      switch (saveSnapshotItem.modifierType) {
        case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD:
        case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE:
          entities[saveSnapshotItem.entity.id] = saveSnapshotItem.entity as TEntity;
          break;
        case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE:
          delete entities[saveSnapshotItem.entity.id];
          break;
        default:
          throw new FlatModels.ErrorControlModels.MedsurfError(
            "notification.type_not_supported",
            FlatModels.ErrorControlModels.ErrorLevel.enum.error,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatStates.EntityBaseState.modifyEntitiesSuccess"
          );
      }
    }

    // Patch State
    state.patchState({
      entities: entities
    } as TStateModel);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  abstract addEntity(state: StateContext<TStateModel>,
                     {entity}: EntityBaseActions.AddEntity<TEntity>): unknown;
  
  abstract updateEntity(state: StateContext<TStateModel>,
                        {entity}: EntityBaseActions.UpdateEntity<TEntity>): unknown;

  abstract removeEntity(state: StateContext<TStateModel>,
                        {entity}: EntityBaseActions.RemoveEntity<TEntity>): unknown;

  //</editor-fold>

  //</editor-fold>
}
